/**
 * @zendir/ui - CardHeader Component
 *
 * Unified card header component for consistent styling across all cards.
 * Uses HeaderIconWithStatus for status-aware icon display.
 *
 * Features:
 * - Consistent header styling across all theme variants (Regular, Bold, Minimal)
 * - Integrated HeaderIconWithStatus for status indicator
 * - Theme-aware accent colors and styling
 * - Optional badge/tag display
 * - Optional subtitle
 * - Accessibility compliant
 *
 * @example
 * ```tsx
 * <CardHeader
 *   icon="satellite-transmit"
 *   title="ISS"
 *   subtitle="SC-001"
 *   status="normal"
 *   badge="LEO"
 *   badgeColor="#3E3CFF"
 * />
 * ```
 */

import React, { memo, useMemo } from "react";
import { useTheme } from "../../theme";
import { HeaderIconWithStatus } from "./HeaderIconWithStatus";
import { AstroIcon, type AstroIconName } from "./AstroIcon";
import { Icon } from "./Icon";
import { type StatusLevel, getStatusColor, safeAccentText } from "../../utils";

// =============================================================================
// StatusBadge - Compact status indicator for accent lines
// =============================================================================

interface StatusBadgeProps {
  status: StatusLevel;
  size?: "bold" | "minimal";
}

function StatusBadge({
  status,
  size = "bold",
}: StatusBadgeProps): React.ReactElement {
  const { tokens } = useTheme();
  const color = tokens.colors.status[status];
  const label = status.charAt(0).toUpperCase() + status.slice(1);
  const isBold = size === "bold";

  // Render status shape
  const renderShape = () => {
    const s = isBold ? 6 : 5;
    switch (status) {
      case "critical":
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <polygon points="6,11 1,2 11,2" fill={color} />
          </svg>
        );
      case "serious":
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <polygon points="6,1 11,6 6,11 1,6" fill={color} />
          </svg>
        );
      case "caution":
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <rect x="1" y="1" width="10" height="10" fill={color} />
          </svg>
        );
      case "standby":
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <circle
              cx="6"
              cy="6"
              r="3.5"
              fill="none"
              stroke={color}
              strokeWidth="2"
            />
          </svg>
        );
      case "off":
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <circle cx="6" cy="6" r="3" fill={color} />
          </svg>
        );
      case "normal":
      default:
        return (
          <svg viewBox="0 0 12 12" width={s} height={s}>
            <circle cx="6" cy="6" r="5" fill={color} />
          </svg>
        );
    }
  };

  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: isBold ? "6px" : "4px",
        padding: isBold ? "3px 10px" : "2px 8px",
        borderRadius: "12px",
        fontSize: isBold ? "0.6875rem" : "0.625rem", // 11px / 10px in rem (AstroUXDS compliant)
        fontWeight: 500, // AstroUXDS: medium weight
        backgroundColor: `${color}20`,
        color: color,
        flexShrink: 0,
        textTransform: "uppercase",
        letterSpacing: "0.3px",
      }}
    >
      {renderShape()}
      {label}
    </span>
  );
}

// =============================================================================
// Types
// =============================================================================

export interface CardHeaderProps {
  /** Icon name (AstroIcon) */
  icon: AstroIconName | string;
  /** Main title text */
  title: string;
  /** Optional subtitle text */
  subtitle?: string;
  /** Status level - determines icon color and status indicator */
  status?: StatusLevel;
  /** Badge text (e.g., orbit type, category) */
  badge?: string;
  /** Badge background color */
  badgeColor?: string;
  /** Show pin indicator */
  isPinned?: boolean;
  /** Icon size override */
  iconSize?: number;
  /** Custom icon color (overrides status-based coloring) */
  iconColor?: string;
  /** Hide status indicator (show plain icon) */
  hideStatusIndicator?: boolean;
  /** Additional className */
  className?: string;
  /** Additional styles */
  style?: React.CSSProperties;
  /** Slot for additional content on the right side */
  rightSlot?: React.ReactNode;
}

// =============================================================================
// CardHeader Component
// =============================================================================

export const CardHeader = memo(function CardHeader({
  icon,
  title,
  subtitle,
  status = "off",
  badge,
  badgeColor,
  isPinned,
  iconSize,
  iconColor,
  hideStatusIndicator = false,
  className,
  style,
  rightSlot,
}: CardHeaderProps) {
  const { tokens, theme } = useTheme();

  // Theme detection
  const _isTransparentTheme =
    theme === "transparent" ||
    theme === "transparent-bold" ||
    theme === "transparent-minimal";
  const isBoldVariant = theme === "transparent-bold";
  const isMinimalVariant = theme === "transparent-minimal";
  const _useGlassMode = isBoldVariant || isMinimalVariant;

  // Glass accent colors — use safeAccentText for text to meet WCAG AA 4.5:1
  const glassAccentColor = useMemo(
    () => safeAccentText(tokens.colors.accent.primary),
    [tokens.colors.accent.primary],
  );
  const glassAccentRaw = tokens.colors.accent.primary; // raw color for non-text usage (gradients, glows)
  const glassAccentMuted = `${glassAccentRaw}66`;

  // Status color (for icon when showing status)
  const statusColor =
    iconColor ??
    (status ? getStatusColor(status) : tokens.colors.text.secondary);

  // Single card heading system (tokens.layout.card.heading)
  const headingConfig = tokens.layout?.card?.heading ?? {
    iconSize: 20,
    iconSizeCompact: 16,
    titleFontSize: "1.125rem",
    titleFontWeight: 500,
    gap: 8,
  };
  const defaultIconSize = isMinimalVariant
    ? headingConfig.iconSizeCompact
    : headingConfig.iconSize;
  const finalIconSize = iconSize ?? defaultIconSize;
  const titleFontSize = headingConfig.titleFontSize;
  const titleFontWeight = headingConfig.titleFontWeight;
  const iconTitleGap = headingConfig.gap;

  // Badge color (use safe accent text if not provided) — ensures 4.5:1 contrast
  const safeBadgeColor = useMemo(
    () =>
      badgeColor
        ? safeAccentText(badgeColor)
        : safeAccentText(tokens.colors.accent.primary),
    [badgeColor, tokens.colors.accent.primary],
  );
  const finalBadgeColor = safeBadgeColor;

  // ═══════════════════════════════════════════════════════════════════════════
  // BOLD VARIANT
  // ═══════════════════════════════════════════════════════════════════════════
  if (isBoldVariant) {
    return (
      <header
        className={className}
        style={{
          position: "relative",
          zIndex: 1,
          marginBottom: tokens.spacing.sm,
          ...style,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div
            style={{ display: "flex", alignItems: "center", gap: iconTitleGap }}
          >
            {/* Icon with status badge — only when icon is provided */}
            {icon &&
              (hideStatusIndicator ? (
                <AstroIcon
                  name={icon as AstroIconName}
                  size={finalIconSize}
                  color={statusColor}
                />
              ) : (
                <HeaderIconWithStatus
                  icon={icon as AstroIconName}
                  size={finalIconSize}
                  status={status}
                />
              ))}

            {/* Title with accent color — card heading system */}
            <h3
              style={{
                margin: 0,
                fontSize: titleFontSize,
                fontWeight: titleFontWeight,
                color: glassAccentColor,
                letterSpacing: "0.5px",
                textShadow: `0 0 20px ${glassAccentMuted}`,
              }}
            >
              {title}
            </h3>

            {/* Pin indicator */}
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>

          {/* Right side: Badge + rightSlot */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: tokens.spacing.sm,
            }}
          >
            {badge && (
              <span
                style={{
                  backgroundColor: `${finalBadgeColor}20`,
                  color: finalBadgeColor,
                  padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
                  borderRadius: tokens.borderRadius.sm,
                  flexShrink: 0,
                  fontSize: tokens.typography.fontSize.xs,
                  fontWeight: tokens.typography.fontWeight.medium,
                }}
              >
                {badge}
              </span>
            )}
            {rightSlot}
          </div>
        </div>

        {/* Subtitle if provided */}
        {subtitle && (
          <span
            style={{
              fontSize: tokens.typography.fontSize.xs,
              color: tokens.colors.text.tertiary,
              marginLeft: finalIconSize + parseInt(tokens.spacing.sm),
            }}
          >
            {subtitle}
          </span>
        )}

        {/* Accent line with optional status badge */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
            marginTop: tokens.spacing.sm,
            marginBottom: tokens.spacing.xs,
          }}
        >
          <div
            style={{
              flex: 1,
              height: "2px",
              background: `linear-gradient(90deg, ${glassAccentRaw} 0%, ${glassAccentMuted} 70%, ${glassAccentMuted}20 100%)`,
              boxShadow: `0 0 8px ${glassAccentMuted}`,
            }}
          />
          {/* Status badge at end of line if status is set and not 'off' */}
          {status && status !== "off" && !hideStatusIndicator && (
            <StatusBadge status={status} size="bold" />
          )}
        </div>
      </header>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // MINIMAL VARIANT
  // ═══════════════════════════════════════════════════════════════════════════
  if (isMinimalVariant) {
    return (
      <header
        className={className}
        style={{
          display: "flex",
          alignItems: "center",
          marginBottom: tokens.spacing.sm,
          position: "relative",
          zIndex: 1,
          ...style,
        }}
      >
        {/* Icon with status badge — only when icon is provided */}
        {icon &&
          (hideStatusIndicator ? (
            <AstroIcon
              name={icon as AstroIconName}
              size={finalIconSize}
              color={statusColor}
              style={{ marginRight: tokens.spacing.sm }}
            />
          ) : (
            <HeaderIconWithStatus
              icon={icon as AstroIconName}
              size={finalIconSize}
              status={status}
              style={{ marginRight: tokens.spacing.sm }}
            />
          ))}

        {/* Title with accent color — card heading system */}
        <h3
          style={{
            margin: 0,
            fontSize: titleFontSize,
            fontWeight: titleFontWeight,
            color: glassAccentColor,
            textShadow: `0 0 16px ${glassAccentMuted}`,
            letterSpacing: "0.02em",
            whiteSpace: "nowrap",
            marginRight: "12px",
          }}
        >
          {title}
        </h3>

        {/* Pin indicator */}
        {isPinned && (
          <Icon
            name="lock"
            size={10}
            color={tokens.colors.accent.primary}
            style={{ marginRight: "8px" }}
          />
        )}

        {/* Accent line with optional status badge */}
        <div
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            gap: tokens.spacing.sm,
            minWidth: 40,
          }}
        >
          <div
            style={{
              flex: 1,
              height: "1px",
              background: `linear-gradient(90deg, ${glassAccentRaw} 0%, ${glassAccentMuted} 70%, ${glassAccentMuted}20 100%)`,
            }}
          />
          {/* Status badge at end of line if status is set and not 'off' */}
          {status && status !== "off" && !hideStatusIndicator && !badge && (
            <StatusBadge status={status} size="minimal" />
          )}
        </div>

        {/* Badge (takes precedence over status badge in minimal view for space) */}
        {badge && (
          <span
            style={{
              backgroundColor: `${finalBadgeColor}20`,
              color: finalBadgeColor,
              padding: "2px 6px",
              borderRadius: tokens.borderRadius.sm,
              flexShrink: 0,
              fontSize: tokens.typography.fontSize.xxs, // 0.625rem / 10px (AstroUXDS compact)
              fontWeight: tokens.typography.fontWeight.medium,
              marginLeft: "8px",
            }}
          >
            {badge}
          </span>
        )}

        {rightSlot}
      </header>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // REGULAR THEMES (Dark, Light, Simple Glass)
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <header
      className={className}
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        marginBottom: tokens.spacing.md,
        ...style,
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: iconTitleGap }}>
        {/* Icon with status badge — only when icon is provided */}
        {icon &&
          (hideStatusIndicator ? (
            <AstroIcon
              name={icon as AstroIconName}
              size={finalIconSize}
              color={statusColor}
            />
          ) : (
            <HeaderIconWithStatus
              icon={icon as AstroIconName}
              size={finalIconSize}
              status={status}
            />
          ))}

        <div style={{ minWidth: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <h3
              style={{
                margin: 0,
                fontSize: titleFontSize,
                fontWeight: titleFontWeight,
                lineHeight: 1.2,
                color: tokens.colors.text.primary,
              }}
            >
              {title}
            </h3>

            {/* Pin indicator */}
            {isPinned && (
              <Icon
                name="lock"
                size={12}
                color={tokens.colors.accent.primary}
              />
            )}
          </div>

          {subtitle && (
            <span
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.text.tertiary,
              }}
            >
              {subtitle}
            </span>
          )}
        </div>
      </div>

      {/* Right side: Status badge + Badge + rightSlot */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: tokens.spacing.sm,
        }}
      >
        {/* Status badge - consistent with Container styling */}
        {status && status !== "off" && !hideStatusIndicator && !rightSlot && (
          <StatusBadge status={status} size="bold" />
        )}
        {badge && (
          <span
            style={{
              backgroundColor: `${finalBadgeColor}20`,
              color: finalBadgeColor,
              padding: `${tokens.spacing.xs} ${tokens.spacing.sm}`,
              borderRadius: tokens.borderRadius.sm,
              flexShrink: 0,
              fontSize: tokens.typography.fontSize.xs,
              fontWeight: tokens.typography.fontWeight.medium,
            }}
          >
            {badge}
          </span>
        )}
        {rightSlot}
      </div>
    </header>
  );
});

export default CardHeader;
